import { Input } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { Download, Info, Share, X } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ShareImageDialog } from "../messages/components";
import ImageModelSelector from "../ImageModelSelector";
import ImageModelParameters from "../ImageModelParameters";

const ImageArtifactSidebar = memo(function ImageArtifactSidebar({
  open,
  onClose,
  imageData,
}) {
  // Chat context for sending messages
  const { handleSendMessage } = useChat();

  // State for the component
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // State for the image edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  const [brushSize, setBrushSize] = useState(40);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState("rgba(147, 51, 234, 0.1)");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Store all drawn paths for perimeter calculation
  const [allPaths, setAllPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);

  // Refs for canvas elements
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Listen for global sidebar close events
  useEffect(() => {
    const handleCloseAllSidebars = (event) => {
      if (open && onClose) {
        onClose();
      }
    };

    window.addEventListener("close-all-sidebars", handleCloseAllSidebars);

    return () => {
      window.removeEventListener("close-all-sidebars", handleCloseAllSidebars);
    };
  }, [open, onClose]);

  // Extract image URL, name, prompt, and metadata from various possible structures
  const { imageUrl, imageName, prompt, metadata } = useMemo(() => {
    if (!imageData)
      return { imageUrl: null, imageName: null, prompt: null, metadata: null };

    let url = null;
    let name = null;
    let imagePrompt = null;
    let imageMeta = null;

    // Handle different data structures
    if (typeof imageData === "string") {
      url = imageData;
    } else if (imageData.result?.data?.url) {
      url = imageData.result.data.url;
      name = imageData.result.data.name;
      imagePrompt = imageData.result.data.prompt;
      imageMeta = imageData.result.metadata;
    } else if (imageData.result?.result?.data?.url) {
      url = imageData.result.result.data.url;
      name = imageData.result.result.data.name;
      imagePrompt = imageData.result.result.data.prompt;
      imageMeta = imageData.result.result.metadata;
    } else if (imageData.result?.url) {
      url = imageData.result.url;
      name = imageData.result.name;
      imagePrompt = imageData.result.prompt;
      imageMeta = imageData.result.metadata;
    } else if (imageData.data?.url) {
      url = imageData.data.url;
      name = imageData.data.name;
      imagePrompt = imageData.data.prompt;
      imageMeta = imageData.metadata;
    } else if (imageData.url) {
      url = imageData.url;
      name = imageData.name;
      imagePrompt = imageData.prompt;
      imageMeta = imageData.metadata;
    }

    return {
      imageUrl: url,
      imageName: name || "Generated Image",
      prompt: imagePrompt,
      metadata: imageMeta,
    };
  }, [imageData]);

  // Initialize canvas when the edit dialog opens
  useEffect(() => {
    if (
      editDialogOpen &&
      canvasRef.current &&
      imageRef.current &&
      containerRef.current
    ) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const image = imageRef.current;

      const setCanvasDimensions = () => {
        canvas.width = image.width;
        canvas.height = image.height;
        context.clearRect(0, 0, canvas.width, canvas.height);
      };

      if (image.complete) {
        setCanvasDimensions();
      } else {
        image.onload = setCanvasDimensions;
      }
    }
  }, [editDialogOpen]);

  // Drawing functions for the canvas
  const startDrawing = (e) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let x, y;
    if (e.type === "mousedown") {
      x = (e.clientX - rect.left) * scaleX;
      y = (e.clientY - rect.top) * scaleY;
    } else if (e.type === "touchstart") {
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
    }

    setCurrentPath([{ x, y }]);

    context.beginPath();
    context.moveTo(x, y);
    context.lineWidth = brushSize;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = currentColor;

    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let x, y;
    if (e.type === "mousemove") {
      x = (e.clientX - rect.left) * scaleX;
      y = (e.clientY - rect.top) * scaleY;
    } else if (e.type === "touchmove") {
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
      e.preventDefault();
    }

    setCurrentPath((prev) => [...prev, { x, y }]);

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    context.closePath();

    if (currentPath.length > 1) {
      setAllPaths((prev) => [...prev, currentPath]);

      setTimeout(() => {
        drawOuterPerimeter();
      }, 50);
    }

    setCurrentPath([]);
    setIsDrawing(false);
  };

  // Function to draw only the outer perimeter of all brush strokes
  const drawOuterPerimeter = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    const allPoints = [];
    allPaths.forEach((path) => {
      path.forEach((point) => {
        const numPoints = 16;
        for (let i = 0; i < numPoints; i++) {
          const angle = (i / numPoints) * 2 * Math.PI;
          const x = point.x + Math.cos(angle) * (brushSize / 2);
          const y = point.y + Math.sin(angle) * (brushSize / 2);
          allPoints.push({ x, y });
        }
      });
    });

    if (allPoints.length === 0) return;

    const hull = convexHull(allPoints);

    if (hull.length > 2) {
      context.save();
      context.setLineDash([8, 4]);
      context.strokeStyle = "rgba(70, 130, 180, 0.9)";
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";

      context.beginPath();
      context.moveTo(hull[0].x, hull[0].y);
      for (let i = 1; i < hull.length; i++) {
        context.lineTo(hull[i].x, hull[i].y);
      }
      context.closePath();
      context.stroke();

      context.restore();
    }
  };

  // Convex hull algorithm (Graham scan)
  const convexHull = (points) => {
    if (points.length < 3) return points;

    let start = points[0];
    for (let i = 1; i < points.length; i++) {
      if (
        points[i].y < start.y ||
        (points[i].y === start.y && points[i].x < start.x)
      ) {
        start = points[i];
      }
    }

    const sortedPoints = points
      .filter((p) => p !== start)
      .sort((a, b) => {
        const angleA = Math.atan2(a.y - start.y, a.x - start.x);
        const angleB = Math.atan2(b.y - start.y, b.x - start.x);
        return angleA - angleB;
      });

    const hull = [start];

    for (const point of sortedPoints) {
      while (
        hull.length > 1 &&
        crossProduct(hull[hull.length - 2], hull[hull.length - 1], point) <= 0
      ) {
        hull.pop();
      }
      hull.push(point);
    }

    return hull;
  };

  // Helper function for cross product
  const crossProduct = (o, a, b) => {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);

    setAllPaths([]);
    setCurrentPath([]);
  };

  // Function to handle image edit submission
  const handleSubmitEdit = async () => {
    if (!canvasRef.current || !editPrompt.trim() || hasSubmitted) return;

    try {
      setHasSubmitted(true);

      const canvas = canvasRef.current;
      const maskDataUrl = canvas.toDataURL("image/png");

      setEditDialogOpen(false);

      await handleSendMessage({
        content: editPrompt,
        contextData: {
          forceImageEditTool: true,
          imageEditData: {
            prompt: editPrompt,
            imageUrls: [imageUrl],
            maskUrl: maskDataUrl,
            size: "auto",
            quality: "auto",
            background: "auto",
            outputFormat: "png",
          },
        },
      });

      setEditPrompt("");
      setHasSubmitted(false);
    } catch (error) {
      console.error("Error submitting image edit:", error);
      setHasSubmitted(false);
    }
  };

  // Handle keyboard events for the edit dialog
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitEdit();
    }
  };

  // Handle download
  const handleDownload = async () => {
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-generated-image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  // Handle share
  const handleShare = () => {
    setShareDialogOpen(true);
  };

  if (!imageUrl) {
    return (
      <div className="flex flex-col h-full w-full bg-background z-[9997] border-l border-border">
        {/* Header with Close Button */}
        <div className="flex items-center justify-center py-6 px-4 border-b border-border relative z-[10000]">
          {/* Title - Centered */}
          <h2 className="text-lg font-semibold">Image Artifact</h2>

          {/* Close Button - Fixed Right */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute right-4 h-8 w-8 p-0 border-0 bg-transparent hover:bg-transparent"
          >
            <X className="h-5 w-5 text-foreground" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden pl-4 pr-4 pt-4">
          <div className="h-full overflow-y-auto">
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <p>No image data available to display.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full w-full bg-background z-[9997] border-l border-border">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between py-6 px-4 border-b border-border relative z-[10000]">
          {/* Title with tooltip - Left aligned */}
          <div className="flex items-start gap-2">
            <h2 className="text-lg font-semibold">{imageName}</h2>
            {prompt && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0 bg-transparent hover:bg-transparent text-muted-foreground hover:text-foreground"
                    >
                      <Info className="h-4 w-4" />
                      <span className="sr-only">Show prompt</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-xs bg-popover/95 backdrop-blur-sm"
                  >
                    <p className="text-sm">{prompt}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Close Button - Fixed Right */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 border-0 bg-transparent hover:bg-transparent"
          >
            <X className="h-5 w-5 text-foreground" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden pl-4 pr-4 pt-4">
          <div className="h-full overflow-y-auto">
            {/* Image display with hover effects */}
            <div
              className="relative w-full group"
              // TODO: Re-enable when image editing is ready
              // className="relative w-full group cursor-pointer"
              // onClick={() => setEditDialogOpen(true)}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <img
                src={imageUrl}
                alt={imageName || "Generated image"}
                className="w-full h-auto rounded-lg shadow-lg transition-transform duration-200 group-hover:scale-105"
                style={{ maxHeight: "60vh", objectFit: "contain" }}
              />

              {/* Hover overlay */}
              {/* TODO: Re-enable when image editing is ready */}
              {/* <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 rounded-lg" /> */}

              {/* Download button - bottom left */}
              <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 bg-transparent hover:bg-black/20 border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                >
                  <Download className="h-5 w-5 text-white" />
                </Button>
              </div>

              {/* Share button - bottom right */}
              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  variant="ghost"
                  className="h-10 px-3 bg-transparent hover:bg-black/20 border-0 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare();
                  }}
                >
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Model information section */}
            {metadata && (metadata.model || metadata.provider) && (
              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h3 className="text-sm font-semibold mb-2">
                  Generation Details
                </h3>
                <div className="space-y-1 text-sm text-muted-foreground">
                  {metadata.model && (
                    <div>
                      <span className="font-medium">Model:</span>{" "}
                      {metadata.model}
                    </div>
                  )}
                  {metadata.provider && (
                    <div>
                      <span className="font-medium">Provider:</span>{" "}
                      {metadata.provider}
                    </div>
                  )}
                  {metadata.timestamp && (
                    <div>
                      <span className="font-medium">Generated:</span>{" "}
                      {new Date(metadata.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Model selector and parameters */}
            <div className="mt-6 space-y-4">
              <div className="border-t border-border pt-6">
                <h3 className="text-sm font-semibold mb-4">
                  Image Generation Settings
                </h3>
                <ImageModelSelector />
              </div>

              <div className="border-t border-border pt-4">
                <ImageModelParameters />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image edit dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent
          className={cn(
            "sm:max-w-[100vw] sm:max-h-[100vh] p-0 m-0 border-0 overflow-hidden z-[99999]",
            "flex flex-col bg-background"
          )}
          hideCloseButton={true}
        >
          <div className="sr-only">
            <DialogTitle>Image Editor</DialogTitle>
          </div>

          {/* Header with controls */}
          <div className="sticky top-0 left-0 right-0 flex justify-between items-center p-4 bg-background">
            {/* Left side with X button */}
            <Button
              size="icon"
              variant="ghost"
              className="h-12 w-12 text-white bg-transparent hover:bg-white/10"
              onClick={() => setEditDialogOpen(false)}
            >
              <X className="h-8 w-8" strokeWidth={3} />
              <span className="sr-only">Close</span>
            </Button>

            {/* Right side with controls */}
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={clearCanvas}
                className="text-xs h-8 border-white text-white bg-black hover:bg-zinc-900"
              >
                Clear Selection
              </Button>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">
                  Brush Size
                </label>
                <Slider
                  value={[brushSize]}
                  min={5}
                  max={50}
                  step={1}
                  onValueChange={(value) => setBrushSize(value[0])}
                  className="w-32"
                />
              </div>
            </div>
          </div>

          {/* Main content area with image and canvas */}
          <div
            className="flex-1 overflow-auto pt-4 pb-20 flex items-center justify-center bg-background"
            ref={containerRef}
          >
            <div className="relative max-h-full max-w-full">
              <img
                ref={imageRef}
                src={imageUrl}
                alt={imageName || "Image to edit"}
                className="max-h-[70vh] max-w-full rounded-lg"
              />
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 w-full h-full"
                style={{
                  cursor: `url("data:image/svg+xml,%3csvg width='${brushSize}' height='${brushSize}' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='${brushSize / 2}' cy='${brushSize / 2}' r='${brushSize / 2 - 1}' fill='none' stroke='%23ffffff' stroke-width='2' opacity='0.8'/%3e%3c/svg%3e") ${brushSize / 2} ${brushSize / 2}, crosshair`,
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
          </div>

          {/* Footer with prompt input */}
          <div className="sticky bottom-0 left-0 right-0 p-4 bg-background">
            <div className="flex justify-center">
              <div className="flex gap-3 items-center w-3/5 max-w-lg">
                <Input
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe what you want to add, remove or replace..."
                  className="flex-grow bg-muted rounded-2xl border-0 px-5 py-4 text-base"
                />
                <Button
                  onClick={handleSubmitEdit}
                  disabled={!editPrompt.trim()}
                  className="bg-purple-600 hover:bg-purple-700 rounded-full h-12 w-12 p-0 flex items-center justify-center aspect-square"
                  size="icon"
                >
                  <span className="sr-only">Edit Image</span>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Select an area of the image to edit, then describe what to change
              in that area
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <ShareImageDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        image={{
          url: imageUrl,
          name: imageName,
          prompt: prompt,
          alt: imageName || "AI generated image",
        }}
      />
    </>
  );
});

ImageArtifactSidebar.displayName = "ImageArtifactSidebar";

export default ImageArtifactSidebar;
